import type { components } from '@connected/api-client/api.generated';

import { Button } from '@connected/ui/components/button';
import { FileInput } from '@connected/ui/components/file';
import { Separator } from '@connected/ui/components/separator';
import { MarkerPin4 } from '@connected/ui/icons/maker-pin';
import { cn } from '@connected/ui/lib/utils';
import { CircleAlert, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';

import { useElectricalProjectFileUpload } from '@/hooks/use-electrical-project-file-upload';

import { ElectricalProjectImage } from './electrical-project-image';

export function PinField({ listQuery, onPinChange, onFloorplanChange, pins, value, electricalProjectVersionId }: {
    listQuery: components['schemas']['ListElectricalProjectFileQueryResultFile'][];
    onPinChange: (pins: { x: number | null; y: number | null }) => void;
    onFloorplanChange: (floorplanFileId: string | null) => void;
    pins: { x: number | null; y: number | null };
    value: string | null;
    electricalProjectVersionId: string;
}) {
    const { t } = useTranslation('common');
    const electricalProjectFileUpload = useElectricalProjectFileUpload({
        endpoint: '/api/v2/electrical/projects/files/upload/floorplan',
        mutationOptions: {
            onMutate: async ({ ElectricalProjectFileId }) => {
                onFloorplanChange(ElectricalProjectFileId!);
            },
            scope: {
                id: electricalProjectVersionId,
            },
        },
    });

    return (
        <>
            {
                value !== '' && value !== null
                    ? (
                            <div className="flex flex-col gap-1">
                                <PinArea
                                    onPinChange={onPinChange}
                                    onFloorplanChange={onFloorplanChange}
                                    pins={pins}
                                    value={value!}
                                />
                                <p className="text-sm font-normal text-text-tertiary">
                                    {t('pin_field.place_pin')}
                                </p>
                            </div>
                        )
                    : (
                            <div className="flex flex-col gap-1">
                                <div className="grid grid-cols-2 gap-x-4 items-center justify-center gap-6 p-6 bg-background-secondary border border-border rounded-md h-96 overflow-auto">
                                    <FileInput
                                        onChange={(fileList) => {
                                            const file = fileList[0];

                                            if (!file)
                                                return;

                                            electricalProjectFileUpload.upload({ file });
                                        }}
                                        accept={electricalProjectFileUpload.accept ?? undefined}
                                        disabled={electricalProjectFileUpload.isLoading}
                                        className={cn('h-64', listQuery.length === 0 ? 'col-span-2  h-full' : 'col-span-1')}
                                        mode="both"
                                    />

                                    {listQuery.length > 0 && (
                                        listQuery.map(file => (
                                            <FloorPlanSelect
                                                key={file.ElectricalProjectFileId!}
                                                floorPlanId={file.ElectricalProjectFileId!}
                                                onChange={onFloorplanChange}
                                            />
                                        ))
                                    )}
                                </div>
                                <p className="text-sm font-normal text-text-tertiary">{t('pin_field.choose_floorplan')}</p>
                            </div>
                        )
            }
        </>
    );
}

function PinArea({ onPinChange, onFloorplanChange, pins, value }: {
    onPinChange: (pins: { x: number | null; y: number | null }) => void;
    onFloorplanChange: (fileId: string | null) => void;
    pins: { x: number | null; y: number | null };
    value: string;
}) {
    const imageRef = useRef<HTMLImageElement>(null);
    const [pinning, setPinning] = useState(false);

    return (
        <div
            className="flex justify-center "
        >

            <div
                className="relative w-full h-96 max-h-96 flex items-center justify-center object-contain border-border border rounded-md bg-background-secondary"
            >

                <TransformWrapper
                    panning={{
                        disabled: pinning,
                    }}
                    pinch={{
                        disabled: pinning,
                    }}
                    smooth={true}

                >
                    <TransformComponent contentClass="w-full h-full " wrapperClass="max-w-full w-full h-96 max-h-96">
                        <button
                            type="button"
                            className={cn('w-full h-full', pinning ? 'cursor-crosshair' : 'cursor-move')}
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                if (!pinning) {
                                    return;
                                }
                                const img = imageRef.current;

                                if (!img)
                                    return;

                                const rect = img.getBoundingClientRect();

                                // Get click position relative to image
                                const x = e.clientX - rect.left;
                                const y = e.clientY - rect.top;

                                // Convert to percentage and apply boundary constraints
                                // This ensures the pin center stays within the image bounds
                                const xPercent = Math.max(2, Math.min(97, Math.round(x / rect.width * 100)));
                                const yPercent = Math.max(2, Math.min(97, Math.round(y / rect.height * 100)));

                                onPinChange({
                                    x: xPercent,
                                    y: yPercent,
                                });
                                setPinning(false);
                            }}
                        >
                            <ElectricalProjectImage
                                alt="Pinnable image"
                                query={{
                                    ElectricalProjectFileId: value,
                                }}
                                ref={imageRef}
                                className="max-h-full max-w-full h-full w-full object-cover  hover:opacity-90 transition-opacity rounded-none"
                                expandable={false}
                            />
                        </button>
                        {pins.x !== null && pins.y !== null && (
                            <div
                                className="absolute w-6 h-6 bg-red-500 rounded-full cursor-pointer flex items-center justify-center "
                                style={{
                                    left: `calc(${pins.x}%)`, // Center the pin horizontally
                                    top: `calc(${pins.y}%)`, // Center the pin vertically
                                    transform: 'translate(-50%, -50%)',
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onPinChange({ x: null, y: null });
                                }}
                            >
                                <CircleAlert
                                    className=" text-white"
                                    size={16}
                                />
                            </div>
                        )}
                    </TransformComponent>
                </TransformWrapper>
                <div
                    className="absolute bottom-2 flex gap-2 p-1 rounded-md z-50 bg-black/80 shadow-lg"
                >
                    <PinControl
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setPinning(!pinning);
                        }}
                        className={cn(pinning && 'bg-background-primary')}
                    >
                        <MarkerPin4 className={cn('stroke-foreground-white group-hover:stroke-foreground-tertiary ', pinning && 'stroke-stone-950')} height="20" width="20" />
                    </PinControl>
                    <Separator className="h-auto h-max-content text-white bg-white" orientation="vertical" />
                    <PinControl onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onFloorplanChange(null);
                    }}
                    >
                        <Trash2 className="stroke-foreground-white group-hover:stroke-foreground-tertiary" height="20" width="20" />
                    </PinControl>
                </div>
            </div>
        </div>
    );
}

function FloorPlanSelect({
    floorPlanId,
    onChange,
}: {
    floorPlanId: string;
    onChange: (fileId: string) => void;
}) {
    return (
        <div
            onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();

                onChange(floorPlanId);
            }}
            className="w-full flex flex-col gap-1 justify-center items-center cursor-pointer"
        >
            <ElectricalProjectImage
                className="max-w-full max-h-64 h-full w-full rounded-md object-cover bg"
                query={{
                    ElectricalProjectFileId: floorPlanId,
                }}
                expandable={false}
                alt="Floorplan Thumbnail"
            />
        </div>
    );
}

function PinControl({ className, children, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
    return (
        <Button
            variant="link"
            type="button"
            className={cn('p-1 h-fit hover:bg-background-tertiary group', className)}
            {...props}
        >
            {children}
        </Button>
    );
}
